import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Box,
  Button,
  Divider,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { useQuestionRecord } from "../../../context/QuestionRecord";
import ROUTES_NAME from "../../../constants/RoutesName";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import { submitApi } from "../../../services/service";
import socket from "../../../socket/socket";

function SubmitSummary({
  sectionNames,
  question,
  setIsOnSubmit,
  sscTimeUp,
  handleRemove,
  currentQuestionId,
  saveBeforeSubmitRef,
}) {
  const { questionRecords } = useQuestionRecord();
  const [submitSummaryD, setSubmitSummaryD] = useState([]);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countdownTime, setCountdownTime] = useState(10);
  const navigate = useNavigate();

  // Save question progress on summary mount (especially important for auto-submit when timer expires)
  useEffect(() => {
    const saveProgress = async () => {
      try {
        if (saveBeforeSubmitRef?.current) {
          await saveBeforeSubmitRef.current();
        }
      } catch (e) {
        console.warn("Could not save question on summary mount:", e);
      }
    };
    saveProgress();
  }, [saveBeforeSubmitRef]);

  // Memoized initial summary creation
  const initialSummary = useMemo(() => {
    if (question?.subjects) {
      return question.subjects.flatMap((subject) => ({
        subject_id: subject.id || 0,
        subject_name: subject.subject_name || "Unnamed Subject",
        sections: (subject.sections || []).map((section) => ({
          section_id: section.id || 0,
          section_name: section.section_name || "Unnamed Section",
          totalQuestion: Number(section.question_count) || 0,
          answered: 0,
          notAns: 0,
          markedWithAns: 0,
          markedWithoutAns: 0,
          notVisited: Number(section.question_count) || 0,
        })),
      }));
    } else if (sectionNames?.length) {
      return [
        {
          subject_id: 0,
          subject_name: "Combined Sections",
          sections: sectionNames.flat().map((section) => ({
            section_id: section.section_id,
            section_name: section.name || "Unnamed Section",
            totalQuestion: 0,
            answered: 0,
            notAns: 0,
            markedWithAns: 0,
            markedWithoutAns: 0,
            notVisited: 0,
          })),
        },
      ];
    }
    return [];
  }, [question, sectionNames]);

  // Initialize or update summary data
  useEffect(() => {
    setSubmitSummaryD(initialSummary);
  }, [initialSummary]);

  // Memoized section question counts
  const updatedSummary = useMemo(() => {
    if (!submitSummaryD.length || !questionRecords?.length)
      return submitSummaryD;

    // Create a map for quick section_id lookup
    const sectionMap = new Map(
      questionRecords.map((q) => [Number(q.section_id), q])
    );

    return submitSummaryD.map((subject) => ({
      ...subject,
      sections: subject.sections.map((section) => {
        const sectionQuestions = questionRecords.filter(
          (q) => Number(q.section_id) === Number(section.section_id)
        );

        const answered = sectionQuestions.reduce(
          (count, q) => (q.attempt_status === "attempted" ? count + 1 : count),
          0
        );

        const notAns = sectionQuestions.reduce(
          (count, q) =>
            q.attempt_status === "not answered" ? count + 1 : count,
          0
        );
        const isEmptyObject = (obj) => !obj || Object.keys(obj).length === 0;

        const markedWithAns = sectionQuestions.reduce(
          (count, q) =>
            q.attempt_status === "marked" &&
              (q.option_id || q.multiple_option_ids?.length || q.answer || q.text || !isEmptyObject(q.match_matrix))
              ? count + 1
              : count,
          0
        );

        const markedWithoutAns = sectionQuestions.reduce(
          (count, q) =>
            q.attempt_status === "marked" &&
              !q.option_id &&
              !q.text &&
              !q.multiple_option_ids?.length &&
              !q.answer && isEmptyObject(q.match_matrix)
              ? count + 1
              : count,
          0
        );

        const notVisited = Math.max(
          0,
          section.totalQuestion -
          (answered + markedWithAns + markedWithoutAns + notAns)
        );

        return {
          ...section,
          answered,
          notAns,
          markedWithAns,
          markedWithoutAns,
          notVisited,
        };
      }),
    }));
  }, [questionRecords, submitSummaryD]);

  // Update summary only when counts actually change
  useEffect(() => {
    const shouldUpdate =
      JSON.stringify(submitSummaryD) !== JSON.stringify(updatedSummary);
    if (shouldUpdate) {
      setSubmitSummaryD(updatedSummary);
    }
  }, [updatedSummary]);

  // const handleRemove = () => {
  //   setIsOnSubmit(false);
  //   sessionStorage.removeItem("isOnsubmit");
  //   window.location.reload();
  // };

  useEffect(() => {
    if (sscTimeUp) {
      const timer = setInterval(() => {
        setCountdownTime((prevTime) => {
          const newTime = prevTime - 1;
          return newTime;
        });
      }, 1000);

      const timeout = setTimeout(() => {
        clearInterval(timer);
        if (sessionStorage.getItem("submitByH")) {
          navigate(ROUTES_NAME.THANK_YOU);
        } else {
          submit("time");
          navigate(ROUTES_NAME.THANK_YOU);
        }
      }, 10000);

      return () => {
        clearTimeout(timeout);
        clearInterval(timer);
      };
    }
  }, [sscTimeUp]);

  const submit = async (type) => {
    const examId = sessionStorage.getItem("examId");
    setLoading(true);

    try {
      const res = await submitApi({ examId, type });
      if (res.success) {
        setLoading(false);
        const examId = sessionStorage.getItem("examId");
        const userId = sessionStorage.getItem("userId");
        socket.emit("changeStatus", {
          examId,
          userId,
          status: "Submitted",
        });
        const branch_id = sessionStorage.getItem("selectedBranch");
        const center_id = sessionStorage.getItem("selectedCenter");
        const userAuth = JSON.parse(sessionStorage.getItem("userAuth"));
        const exam_name = sessionStorage.getItem("exam_name");
        sessionStorage.clear();
        sessionStorage.setItem("exam_name", exam_name);
        sessionStorage.setItem("userId", userId);
        sessionStorage.setItem("selectedBranch", branch_id);
        sessionStorage.setItem("selectedCenter", center_id);
        sessionStorage.setItem("exam_name", exam_name);
        sessionStorage.setItem("userAuth", JSON.stringify(userAuth));
        sessionStorage.setItem("candidateLoggedinToDashboard", true);

        if (userId === "4660") {
          navigate(ROUTES_NAME.EXAM_SCORE, {
            state: { examResult: JSON.parse(res.examResult).result },
          });
          sessionStorage.setItem("examId", examId);
        } else {
        navigate(ROUTES_NAME.THANK_YOU);
        }
      }
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    socket.connect();
    return () => {
      socket.disconnect();
    };
  }, []);

  return (
    <>
      {submitSummaryD.length === 0 && <Loader />}

      {loading && (
        <Loader
          message={
            "Your exam will be submitted in a few seconds, please wait..."
          }
        />
      )}
      <div>
        {agree ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "80vh",
                width: "100%",
                px: 2,
              }}
            >
              <Box sx={{ width: "100%", maxWidth: "400px" }}>
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: "center",
                    fontWeight: "600",
                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                    mb: 3
                  }}
                >
                  Are you sure to submit exam?
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      submit("user");
                    }}
                    sx={{ minWidth: 120, borderRadius: "8px" }}
                  >
                    Yes
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleRemove}
                    sx={{ minWidth: 120, borderRadius: "8px" }}
                  >
                    No
                  </Button>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Typography
              variant="h6"
              sx={{
                mt: 2,
                fontWeight: "bold",
                textAlign: "center",
                borderBottom: "1px solid black",
                paddingBottom: "10px",
              }}
            >
              Exam Summary
            </Typography>
            {sscTimeUp && countdownTime && (
              <Typography
                variant="body1"
                sx={{
                  mt: 2,
                  fontWeight: "600",
                  textAlign: "center",
                  borderBottom: "1px solid black",
                  paddingBottom: "10px",
                }}
              >
                {sessionStorage.getItem("submitByH")
                  ? "Your exam has been submitted by the head authority!"
                  : "Time Up"}{" "}
                : {countdownTime}s
              </Typography>
            )}

            <Box
              sx={{
                overflowY: "auto",
                height: {
                  xs: countdownTime === 10 ? "calc(100vh - 280px)" : "calc(100vh - 140px)",
                  sm: "calc(100vh - 220px)",
                  md: "calc(100vh - 200px)"
                },
                pb: 2,
              }}
            >
              {submitSummaryD.map((subject, sIdx) => (
                <Box p={{ xs: 1, sm: 2 }} key={sIdx}>
                  <Typography sx={{ py: 1, fontWeight: "600", fontSize: { xs: "0.9rem", sm: "1rem" }, color: "#333" }}>
                    Subject : {subject.subject_name}
                  </Typography>
                  <TableContainer component={Paper} sx={{ boxShadow: 1, borderRadius: "8px", overflowX: "auto" }}>
                    <Table size="small" aria-label="exam summary table" sx={{ minWidth: { xs: 750, sm: "auto" } }}>
                      <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "600", fontSize: { xs: "0.75rem", sm: "0.85rem" }, py: 1.5 }}>Section Name</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "600", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Total</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "600", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Answered</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "600", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Not Ans</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "600", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Marked</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "600", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Ans & Marked</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "600", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Not Visited</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subject.sections?.map((section, index) => (
                          <TableRow key={index} hover>
                            <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>{section.section_name}</TableCell>
                            <TableCell align="center" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>{section.totalQuestion}</TableCell>
                            <TableCell align="center" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>{section.answered}</TableCell>
                            <TableCell align="center" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>{section.notAns}</TableCell>
                            <TableCell align="center" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>{section.markedWithoutAns}</TableCell>
                            <TableCell align="center" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>{section.markedWithAns}</TableCell>
                            <TableCell align="center" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>{section.notVisited}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>

            {countdownTime === 10 && !sscTimeUp ? (
              <Box
                sx={{
                  position: "fixed",
                  width: "100%",
                  bottom: 0,
                  borderTop: "1px solid #ccc",
                  py: 1.5,
                  bgcolor: "#fff",
                  boxShadow: "0 -2px 10px rgba(0,0,0,0.05)",
                  zIndex: 10,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    margin: "auto",
                    width: { xs: "90%", sm: "80%", md: "60%" },
                    textAlign: "center",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    lineHeight: 1.4,
                    color: "#555",
                  }}
                >
                  Are you sure to submit this Subject? Click 'Yes' to proceed;
                  Click 'No' to go back. Once submitted, you cannot edit your responses.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 1.5,
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setAgree(true)}
                    sx={{ minWidth: 100, textTransform: "none", borderRadius: "6px" }}
                  >
                    Yes
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleRemove}
                    sx={{ minWidth: 100, textTransform: "none", borderRadius: "6px" }}
                  >
                    No
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  position: "fixed",
                  width: "100%",
                  bottom: 0,
                  borderTop: "1px solid #ccc",
                  py: 2,
                  display: "flex",
                  justifyContent: "center",
                  bgcolor: "#fff",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    submit("time");
                  }}
                  sx={{ minWidth: 150, textTransform: "none", borderRadius: "8px", fontWeight: "bold" }}
                >
                  Submit Exam
                </Button>
              </Box>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default SubmitSummary;